/**
 * Lightweight client-side fetch wrapper for our own /api/* endpoints.
 * Sends cookies (for the better-auth session) and rejects with a typed error
 * for non-2xx responses.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    /** Optional human-readable diagnostic from the server. Surfaced in the
     *  dashboard when the code itself doesn't map to a friendly message. */
    public detail?: string,
    message?: string,
    /** Full parsed JSON body of the error response. Lets endpoint-specific
     *  callers fish out custom fields (e.g. pixel validation surfaces
     *  metaError / metaCode / httpStatus from Meta's CAPI response) without
     *  the base `request()` helper having to know about every shape. */
    public raw?: Record<string, unknown>,
  ) {
    super(message || code);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init?.headers || {}),
    },
    ...init,
  });
  if (!res.ok) {
    let code = 'request_failed';
    let detail: string | undefined;
    let raw: Record<string, unknown> | undefined;
    try {
      const body = (await res.json()) as { error?: string; detail?: string } & Record<string, unknown>;
      code = body.error || code;
      detail = body.detail;
      raw = body;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, code, detail, undefined, raw);
  }
  // 204 No Content (e.g. DELETE responses) — return undefined typed as T so
  // callers that expect `void` don't crash on `res.json()` of an empty body.
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

export interface Link {
  id: string;
  shortCode: string;
  /** Subdomain label when the parent domain has wildcard enabled — empty
   *  string for apex / non-wildcard links. */
  subdomain?: string;
  targetUrl: string;
  title: string | null;
  defaultAction: 'escape' | 'redirect' | 'page';
  /** Set when the link's action is 'page' and a landing page is attached. */
  pageId?: string | null;
  /** True when Poof Shield is on for this link. */
  shieldEnabled?: boolean;
  /** True when the 18+ confirmation interstitial is on. Surfaced on the
   *  dashboard list as a "18+" badge so creators can spot NSFW-gated
   *  links at a glance. */
  ageGateEnabled?: boolean;
  /** Per-link variation counts so the dashboard can show badges
   *  without re-fetching the full variations array. */
  variationCounts?: { conditional: number; weighted: number };
  status: 'active' | 'paused' | 'archived';
  domainId: string | null;
  fullUrl: string;
  createdAt: string | number | Date;
}

export interface ShieldConfig {
  bots: { generic: boolean; previewers: boolean; metaCrawlers: boolean; metaAsn: boolean };
  countries: { mode: 'off' | 'allow' | 'block'; list: string[] };
  decoy: {
    type: 'not_found' | 'redirect' | 'page';
    url?: string;
    pageId?: string;
  };
}

// Re-export the condition types we share with @poof/shared so the
// dashboard editor doesn't need its own copy. Variation is the unified
// "alternative action per visitor" — replaces both LinkVariant (A/B)
// and RoutingRule (conditional) which used to live here.

export type VariationDevice = 'mobile' | 'tablet' | 'desktop';
export type VariationHostApp =
  | 'instagram'
  | 'threads'
  | 'facebook'
  | 'messenger'
  | 'tiktok'
  | 'twitter'
  | 'linkedin'
  | 'pinterest'
  | 'reddit'
  | 'snapchat'
  | 'direct';

export interface VariationConditions {
  countries?: { mode: 'in' | 'not_in'; list: string[] };
  devices?: VariationDevice[];
  hostApps?: VariationHostApp[];
  referrers?: { mode: 'contains' | 'not_contains'; list: string[] };
  hours?: { start: number; end: number; tz: string };
}

export interface VariationAction {
  targetUrl?: string;
  pageId?: string;
  escapeIab?: boolean;
}

export interface Variation {
  id: string;
  enabled: boolean;
  name?: string;
  position: number;
  /** Omitted / empty = pure weighted (Pass 2). */
  conditions?: VariationConditions;
  /** 0+ integer. 0 / omitted = no weighted-pick eligibility. */
  weight?: number;
  action: VariationAction;
}

export interface LinkDetail extends Link {
  hostname: string;
  pageId?: string | null;
  escapeIab?: boolean;
  shieldEnabled?: boolean;
  shieldConfig?: ShieldConfig;
  variations?: Variation[];
  /** Show an "Are you 18+?" interstitial before the link's action. */
  ageGateEnabled?: boolean;
  /** How long a visitor's confirmation persists. */
  ageGateMemory?: 'always' | 'session' | '24h';
  /** Also gate the Insta-Shield decoy (visitors that Shield catches). */
  ageGateOnDecoy?: boolean;
  updatedAt?: string | number | Date;
}

export const links = {
  list: () => request<{ links: Link[] }>('/api/links'),
  /**
   * Per-link visit counts for the last N days. One AE query
   * server-side, returns a map of `<host>|<path>` → count which the
   * Overview joins against the link list to render "X visits / 7d"
   * badges. Counts only primary user actions, not page-click events.
   */
  /**
   * Per-link visit counts + org-wide daily breakdown for the dashboard
   * stat cards. The `daily` series spans 2× the period so the
   * frontend can split current half vs previous half for the delta
   * indicator on the Visits stat card and render the sparkline.
   */
  clickCounts: (days = 7) =>
    request<{
      counts: Record<string, number>;
      daily: Array<{ day: string; clicks: number }>;
      days: number;
    }>(`/api/links/click-counts?days=${days}`),
  create: (input: {
    targetUrl: string;
    title?: string;
    shortCode?: string;
    domainId?: string | null;
    /** DNS label like `xyz` for `xyz.brand.com`. Only honored when the
     *  parent domain has `wildcardEnabled = true`. */
    subdomain?: string;
  }) =>
    request<Link>('/api/links', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  get: (id: string) => request<LinkDetail>(`/api/links/${id}`),
  update: (
    id: string,
    input: {
      targetUrl?: string;
      title?: string | null;
      domainId?: string | null;
      pageId?: string | null;
      defaultAction?: 'escape' | 'redirect' | 'page';
      escapeIab?: boolean;
      shieldEnabled?: boolean;
      shieldConfig?: ShieldConfig;
      variations?: Variation[];
      ageGateEnabled?: boolean;
      ageGateMemory?: 'always' | 'session' | '24h';
      ageGateOnDecoy?: boolean;
    },
  ) =>
    request<LinkDetail>(`/api/links/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  delete: (id: string) =>
    request<void>(`/api/links/${id}`, { method: 'DELETE' }),
  /**
   * Clone an existing link, keeping every setting (target URL, attached
   * page, Poof Shield config, variations, etc.) except the short code.
   *
   * Use case: a customer has a link tuned exactly how they want it and
   * needs the SAME setup on a different slug (one per IG post, one per
   * platform, one per A/B-tested bio CTA, etc.). Server-side handles
   * the deep-copy so the dashboard doesn't have to round-trip the full
   * shield/variations payload.
   */
  duplicate: (id: string, input: { shortCode?: string; title?: string }) =>
    request<Link>(`/api/links/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// ---------------------------------------------------------------------------
// Domains
// ---------------------------------------------------------------------------

export interface Domain {
  id: string;
  hostname: string;
  source: 'subdomain' | 'byo' | 'purchased';
  sslStatus: 'pending' | 'active' | 'failed' | 'unknown';
  verificationStatus: 'pending' | 'active' | 'failed' | 'unknown';
  /** True when a `*.hostname` sibling is also registered with CF, so
   *  any subdomain of `hostname` routes through Poof. */
  wildcardEnabled?: boolean;
  /** Wildcard SSL state. Only meaningful when `wildcardEnabled = true`. */
  wildcardSslStatus?: 'pending' | 'active' | 'failed' | 'unknown';
  wildcardVerificationStatus?: 'pending' | 'active' | 'failed' | 'unknown';
  createdAt?: string | number | Date;
}

export interface DnsInstructions {
  cname: { name: string; value: string };
  ownership?: { type: 'txt' | 'http'; name: string; value: string };
}

export interface DomainWithDns extends Domain {
  /** When wildcard is enabled both sets of DNS records are returned;
   *  otherwise only `apex` is populated. */
  dns?: { apex: DnsInstructions | null; wildcard: DnsInstructions | null };
  cfRaw?: { sslMethod?: string; sslStatusRaw?: string; statusRaw?: string } | null;
  warning?: string;
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

/** Optional filters for the /api/analytics summary endpoint. Each filter
 *  AND-stacks; unset filters are ignored. Backend validates everything and
 *  silently drops invalid values, so passing junk won't 4xx — it just won't
 *  filter. Cheap to pass freely from the URL. */
export interface AnalyticsFilters {
  country?: string;
  device?: string;
  hostApp?: string;
  browser?: string;
  /** Link ID (UUID). Backend resolves to (host, path) via D1 and drops
   *  silently if the link doesn't belong to the active org. */
  link?: string;
}

export type AnalyticsRange = 'today' | 'yesterday' | '7d' | '30d' | '90d';

export interface AnalyticsSummary {
  totalClicks: number;
  days: number;
  /** Which range the server actually served (post-validation). */
  range: AnalyticsRange;
  /** Bucket size of `byDay`. 'hour' for today/yesterday, 'day' otherwise.
   *  Frontend uses this to format the chart's X-axis labels. */
  granularity: 'day' | 'hour';
  byDay: Array<{ day: string; clicks: number }>;
  byCountry: Array<{ country: string; clicks: number }>;
  byHostApp: Array<{ hostApp: string; clicks: number }>;
  /** Browser bucket: chrome / safari / firefox / edge / samsung /
   *  opera / in-app / other. Populated 2026-05-18 onward. */
  byBrowser: Array<{ browser: string; clicks: number }>;
  /** Device class: mobile / tablet / desktop. */
  byDevice: Array<{ device: string; clicks: number }>;
  /** Top 20 links by clicks in the window, hydrated with link ID + label
   *  from D1. AE rows whose (host, path) no longer match a live link
   *  (deleted, renamed) are dropped server-side. */
  byLink: Array<{
    linkId: string;
    host: string;
    shortCode: string;
    /** "<host>/<shortCode>" — matches how the link looks in social. */
    label: string;
    clicks: number;
  }>;
  /** Echo of the filters the server actually applied (post-validation).
   *  Frontend uses this to render filter pills without re-parsing URL. */
  appliedFilters: {
    country?: string;
    device?: string;
    hostApp?: string;
    browser?: string;
    /** Link label (`host/shortCode`) included so the pill can render
     *  it without a second API call. */
    link?: { id: string; label: string };
  };
  /** Today-so-far vs yesterday-up-to-the-same-wall-clock-minute. The
   *  apples-to-apples comparison — comparing partial today vs full
   *  yesterday would always print a misleading negative every morning. */
  delta: { today: number; yesterdaySameTime: number };
  /** Rolling 7-day comparison. Last 7 days vs the 7 days before that.
   *  Rolling rather than calendar-week avoids the "Monday morning
   *  shows -90% vs full last week" trap. */
  weekDelta: { thisWeek: number; lastWeek: number };
  /** Clicks in the last 5 minutes. Window deliberately wider than AE's
   *  ~30s ingest latency so the metric reads as genuinely live. */
  liveNow: number;
  asOf: string;
}

export interface BlockCtrRow {
  blockId: string;
  blockType: string;
  label: string;
  /** Total taps on this block (refreshes + repeat-taps all count). */
  clicks: number;
  /** clicks / parent page's uniqueVisitors — average taps per visitor on
   *  this block. Can legitimately exceed 1.0 when visitors tap the same
   *  block multiple times. */
  ctr: number;
}

export interface VariantCtrBreakdown {
  variantId: string;
  variantLabel: string;
  pageId: string;
  pageName: string;
  /** Every page render for this variant — refreshes count. */
  views: number;
  /** Distinct visitors who saw this variant. */
  uniqueVisitors: number;
  /** Total taps across all blocks for this variant. */
  clicks: number;
  /** clicks / uniqueVisitors. Can exceed 1.0 on tap-heavy variants. */
  overallCtr: number;
  perBlock: BlockCtrRow[];
}

export interface PageCtrAnalytics {
  pageId: string;
  pageName: string;
  /** Every page render — refreshes count. */
  views: number;
  /** Distinct `_fbp` cookies that saw the page. The honest "how many
   *  people visited" number. */
  uniqueVisitors: number;
  /** Total taps across all blocks. */
  clicks: number;
  /** clicks / uniqueVisitors. Can exceed 1.0 — visitors often tap
   *  multiple blocks. */
  overallCtr: number;
  perBlock: BlockCtrRow[];
  /** Set when the link is running an A/B test serving pages. Each item
   *  carries that variant's own page name + per-block CTR — used by the
   *  dashboard to render side-by-side variant comparison cards. */
  perVariant?: VariantCtrBreakdown[];
}

/** Per-link analytics. The per-link endpoint doesn't return byLink (the
 *  view is already scoped to one link) or appliedFilters (no filter UI on
 *  the link detail page), so we Omit those before extending. */
export interface LinkAnalytics extends Omit<AnalyticsSummary, 'byLink' | 'appliedFilters'> {
  linkId: string;
  /** Present only when the link's default action is 'page'. */
  pageCtr?: PageCtrAnalytics;
}

export type ShieldCategory = 'meta_scrapers' | 'bots_and_previewers' | 'country_filtered';

export interface LinkShieldAnalytics {
  linkId: string;
  days: number;
  totalBlocked: number;
  totalAll: number;
  byCategory: Array<{ category: ShieldCategory; count: number }>;
  byDay: Array<{ day: string; count: number }>;
  asOf: string;
}

export const analytics = {
  /** Fetch the org-level summary. Pass either `range` (today/yesterday)
   *  OR `days` (a multi-day window) — range wins if both are set. */
  summary: (
    rangeOrDays: AnalyticsRange | number = 7,
    refresh = false,
    filters?: AnalyticsFilters,
  ) => {
    const qs = new URLSearchParams();
    if (typeof rangeOrDays === 'string') {
      if (rangeOrDays === 'today' || rangeOrDays === 'yesterday') {
        qs.set('range', rangeOrDays);
      } else {
        // '7d' | '30d' | '90d' → map back to the legacy `days=N` shape so
        // both old and new clients hit the same backend code path.
        qs.set('days', rangeOrDays.replace('d', ''));
      }
    } else {
      qs.set('days', String(rangeOrDays));
    }
    if (refresh) qs.set('refresh', '1');
    if (filters?.country) qs.set('country', filters.country);
    if (filters?.device) qs.set('device', filters.device);
    if (filters?.hostApp) qs.set('hostApp', filters.hostApp);
    if (filters?.browser) qs.set('browser', filters.browser);
    if (filters?.link) qs.set('link', filters.link);
    return request<AnalyticsSummary>(`/api/analytics?${qs.toString()}`);
  },
  link: (
    id: string,
    rangeOrDays: AnalyticsRange | number = 7,
    refresh = false,
  ) => {
    const qs = new URLSearchParams();
    if (typeof rangeOrDays === 'string') {
      if (rangeOrDays === 'today' || rangeOrDays === 'yesterday') {
        qs.set('range', rangeOrDays);
      } else {
        qs.set('days', rangeOrDays.replace('d', ''));
      }
    } else {
      qs.set('days', String(rangeOrDays));
    }
    if (refresh) qs.set('refresh', '1');
    return request<LinkAnalytics>(`/api/analytics/links/${id}?${qs.toString()}`);
  },
  shield: (id: string, days = 7, refresh = false) =>
    request<LinkShieldAnalytics>(
      `/api/analytics/links/${id}/shield?days=${days}${refresh ? '&refresh=1' : ''}`,
    ),
};

export const domains = {
  list: () => request<{ domains: Domain[] }>('/api/domains'),
  /** Register a custom hostname under the org's Cloudflare for SaaS zone.
   *  Wildcard (`*.brand.com`) registration was removed — Cloudflare gates
   *  wildcard Custom Hostnames behind the Enterprise plan. Customers who
   *  want multiple subdomains add each as its own apex-level entry. */
  create: (hostname: string) =>
    request<DomainWithDns>('/api/domains', {
      method: 'POST',
      body: JSON.stringify({ hostname }),
    }),
  get: (id: string) => request<DomainWithDns>(`/api/domains/${id}`),
  delete: (id: string) =>
    request<void>(`/api/domains/${id}`, { method: 'DELETE' }),
  autoAddDns: (id: string) =>
    request<{
      ok: boolean;
      zone: string;
      records: Array<{
        type: 'CNAME' | 'TXT';
        name: string;
        status: 'created' | 'already_present' | 'updated' | 'error';
        message?: string;
      }>;
    }>(`/api/domains/${id}/auto-add`, { method: 'POST' }),
};

// ---------------------------------------------------------------------------
// Integrations — Cloudflare DNS auto-write
// ---------------------------------------------------------------------------

export interface CloudflareIntegration {
  connected: boolean;
  zones?: Array<{ id: string; name: string }>;
  accountLabel?: string;
  connectedAt?: number | string | Date;
}

// ---------------------------------------------------------------------------
// DNS lookups — used by the UI to detect whether a hostname is on Cloudflare
// before we surface the CF integration CTA.
// ---------------------------------------------------------------------------

export interface NsCheckResult {
  host: string;
  zone: string | null;
  isCloudflare: boolean;
  nameservers: string[];
  cached: boolean;
}

export const dns = {
  checkNs: (host: string) =>
    request<NsCheckResult>(`/api/dns/check-ns?host=${encodeURIComponent(host)}`),
};

// ---------------------------------------------------------------------------
// Members + invitations
// ---------------------------------------------------------------------------

export type MemberRole = 'owner' | 'admin' | 'member';

export interface MemberRow {
  id: string;
  userId: string;
  role: MemberRole;
  email: string;
  name: string;
  image: string | null;
  createdAt: number | string | Date;
  isYou: boolean;
}

export interface InvitationRow {
  id: string;
  email: string;
  role: MemberRole;
  status: string;
  expiresAt: number | string | Date;
}

export interface MembersResponse {
  plan: 'free' | 'pro' | 'agency';
  role: MemberRole;
  memberLimit: number;
  members: MemberRow[];
  invitations: InvitationRow[];
}

// ---------------------------------------------------------------------------
// Landing pages
// ---------------------------------------------------------------------------

export interface PageSummary {
  id: string;
  name: string;
  status: 'draft' | 'published';
  createdAt: number | string | Date;
  updatedAt: number | string | Date;
  /** Number of links currently routing to this page as their primary
   *  lander. The dashboard's delete affordance is disabled when this
   *  OR `decoyCount` is > 0 (the API hard-rejects the delete too, so
   *  customers can't accidentally nuke a page that's serving live
   *  traffic). */
  linkCount: number;
  /** Number of links currently using this page as their Poof Shield
   *  decoy (Shield enabled + decoy.type=page + decoy.pageId matches). */
  decoyCount: number;
}

export type PageBlockType =
  | 'avatar'
  | 'heading'
  | 'paragraph'
  | 'link-button'
  | 'social-row'
  | 'image'
  | 'divider'
  | 'countdown'
  | 'stock-pill'
  | 'video-embed'
  | 'sticky-cta'
  | 'link-list'
  | 'carousel'
  | 'live-status'
  | 'instagram-bio'
  | 'spacer';

export interface PageBlock {
  id: string;
  type: PageBlockType;
  props: Record<string, unknown>;
}

export interface PageTheme {
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily: 'sans' | 'sf-pro' | 'serif' | 'mono';
  radius: 'sharp' | 'soft' | 'pill';
  backgroundImage?: string;
  backgroundOverlay?: boolean;
}

export interface PageMeta {
  title?: string;
  description?: string;
  ogImage?: string;
  /** Browser-tab / iOS home-screen icon. Empty → renderer uses the Poof
   *  cloud (💨) so no page ends up with a blank-document tab icon. */
  favicon?: string;
}

export interface PageDetail {
  id: string;
  name: string;
  status: 'draft' | 'published';
  blocks: PageBlock[];
  theme: PageTheme;
  meta: PageMeta;
  createdAt: number | string | Date;
  updatedAt: number | string | Date;
}

export const pages = {
  list: () => request<{ pages: PageSummary[] }>('/api/pages'),
  create: (input: { name: string; templateId: string }) =>
    request<{ id: string; name: string; status: 'draft' | 'published' }>('/api/pages', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  get: (id: string) => request<PageDetail>(`/api/pages/${id}`),
  update: (
    id: string,
    input: Partial<Pick<PageDetail, 'name' | 'blocks' | 'theme' | 'meta' | 'status'>>,
  ) =>
    request<{ ok: true }>(`/api/pages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  delete: (id: string) => request<void>(`/api/pages/${id}`, { method: 'DELETE' }),
};

/**
 * Compress an image in-browser before upload.
 *
 * iPhone/Android photos are routinely 4–12 MB and easily 4000+ px on the
 * long edge. The bio-link lander is at most 520 px wide on mobile, and
 * the hero background extends to viewport width (≤480 px in practice).
 * 1280 px on the long edge is 2-2.5× retina at those display sizes — crisp.
 *
 * Two-stage compression:
 *   1. Resize to MAX_DIM on the long edge (if larger) — this is the
 *      biggest byte saving; a 4000×6000 JPEG drops to 853×1280 with a
 *      proportional pixel-count reduction of ~22×.
 *   2. Ratchet JPEG quality down until under TARGET_BYTES.
 *
 * Skipped for SVG (vector, no benefit) and GIF (animation we'd lose).
 *
 * **Always runs canvas re-encoding** even for files already under the
 * target — a 3.5 MB JPEG at 4000 px is still wasteful on 3G even if it
 * "passes" a generous size threshold. The old behavior (skip-if-under-
 * target with target=4MB) let pictures like camilacow's 3.72 MB hero
 * background through untouched, causing a ~20s load on 3G.
 *
 * Performance: canvas re-encoding of a 3-5 MB JPEG runs in ~200-400ms
 * on a typical mobile device. Trivial compared to the upload time it
 * saves (3.72 MB on 3G upload = ~25s; 200 KB = ~1.3s).
 */
/** Probe canvas WebP support once per session. Safari 14+ / Chrome 32+ /
 *  Firefox 65+ all support `canvas.toBlob('image/webp')`. Older browsers
 *  return null/PNG instead. Cached so we don't do the probe on every
 *  upload — same browser will answer the same way every time. */
let _webpEncodeSupport: boolean | null = null;
async function canEncodeWebp(): Promise<boolean> {
  if (_webpEncodeSupport !== null) return _webpEncodeSupport;
  if (typeof document === 'undefined') return false;
  const c = document.createElement('canvas');
  c.width = 1;
  c.height = 1;
  const blob = await new Promise<Blob | null>((resolve) =>
    c.toBlob(resolve, 'image/webp', 0.5),
  );
  _webpEncodeSupport = !!blob && blob.type === 'image/webp';
  return _webpEncodeSupport;
}

async function compressImageInBrowser(file: File, targetBytes: number): Promise<File> {
  // Bypass for formats we can't safely re-encode without quality loss.
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
  if (typeof document === 'undefined' || typeof Image === 'undefined') return file;
  // Trust files that are ALREADY small AND likely already-optimised
  // (under 60 KB) — skip the canvas roundtrip to avoid blowing them up.
  // Lowered from 80 KB now that WebP lets us legitimately get below 100 KB
  // for typical photos at the dashboard's preview size.
  if (file.size <= 60 * 1024) return file;

  const objUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image_load_failed'));
      el.src = objUrl;
    });

    // 1280 long-edge: 2.5× retina at 520 px (the mobile lander max
    // width), 2.6× retina at 480 px (typical hero render width).
    // Drop further if you want even smaller payloads — every 2× pixel
    // count reduction = roughly 4× byte reduction.
    const MAX_DIM = 1280;
    let w = img.naturalWidth || img.width;
    let h = img.naturalHeight || img.height;
    if (w <= 0 || h <= 0) return file;
    if (w > MAX_DIM || h > MAX_DIM) {
      const scale = MAX_DIM / Math.max(w, h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, w, h);

    // Prefer WebP — typically 25-35% smaller than JPEG at the same
    // perceived quality, all modern browsers (iOS 14+, Android Chrome,
    // Firefox 65+) decode it natively. Fall back to JPEG only if the
    // uploading browser can't encode WebP (older Safari mainly).
    const useWebp = await canEncodeWebp();
    const outMime = useWebp ? 'image/webp' : 'image/jpeg';
    const outExt = useWebp ? 'webp' : 'jpg';

    // Ratchet quality down until we're under the limit. Drops alpha
    // when falling back to JPEG; WebP preserves transparency.
    for (const quality of [0.82, 0.72, 0.62, 0.5]) {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, outMime, quality),
      );
      if (blob && blob.size <= targetBytes) {
        return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.' + outExt, {
          type: outMime,
          lastModified: Date.now(),
        });
      }
    }

    // Fallback: take the smallest we got even if still over the limit.
    // At MAX_DIM=1280 and quality 0.5, this case is rare — would
    // require a photo that's mostly noise (very poor compressibility).
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outMime, 0.5),
    );
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.' + outExt, {
      type: outMime,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

export const upload = {
  image: async (file: File): Promise<{ url: string; key: string; size: number; mime: string }> => {
    // 250 KB target. With WebP encoding, a 1280-long-edge photo at q=0.82
    // typically lands around 120-200 KB — well under the limit at no
    // visible quality loss. Older browsers that fall back to JPEG use
    // the full 250 KB envelope, matching the prior 400 KB target's
    // roughly equivalent bit-rate.
    const COMPRESS_TARGET = 250 * 1024;
    const prepared = await compressImageInBrowser(file, COMPRESS_TARGET);

    const form = new FormData();
    form.append('file', prepared);
    const res = await fetch('/api/upload', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      let code = 'upload_failed';
      try {
        const body = (await res.json()) as { error?: string };
        code = body.error || code;
      } catch {
        // ignore
      }
      throw new ApiError(res.status, code);
    }
    return res.json();
  },
};

// (Cross-link variations overview endpoint removed 2026-05-18 — the
// /app/variations dashboard page was redundant with the Overview link
// list. Variations are now edited per-link in the link editor.)

export const members = {
  list: () => request<MembersResponse>('/api/members'),
  invite: (email: string, role: MemberRole = 'member') =>
    request<{ ok: true }>('/api/members/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),
  setRole: (id: string, role: MemberRole) =>
    request<{ ok: true }>(`/api/members/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  remove: (id: string) =>
    request<void>(`/api/members/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Admin (super-admin only — gated by ADMIN_EMAILS env var on the server)
// ---------------------------------------------------------------------------

export interface BotResearchRow {
  timestamp: string;
  country: string;
  hostApp: string;
  actionKind: string;
  shieldClass: string;
  orgId: string;
  linkId: string;
  ipHash: string;
  asn: string;
  userAgent: string;
  headers: Record<string, string>;
  isSuspicious: boolean;
  isEu: boolean;
  cfBotScore: number | null;
}

export interface BotResearchResponse {
  host: string;
  path: string;
  days: number;
  total: number;
  rows: BotResearchRow[];
  byAsn: Array<{ asn: string; count: number }>;
  byAction: Array<{ actionKind: string; count: number }>;
  byClassification: Array<{ shieldClass: string; count: number }>;
  asOf: string;
}

export interface AdminClient {
  organizationId: string;
  name: string;
  ownerEmail: string | null;
  /** Used by the impersonate endpoint to know whose session to mint. */
  ownerUserId: string | null;
  plan: 'free' | 'pro' | 'agency';
  /** Unix ms. */
  createdAt: number;
  memberCount: number;
  linkCount: number;
  /** Last 30 days. */
  clicks30d: number;
  /** Unix ms of the most recent session activity across any org member.
   *  Null when no one ever signed in (e.g. invited members who haven't
   *  accepted). */
  lastSeen: number | null;
}

export const admin = {
  status: () => request<{ isAdmin: boolean }>('/api/admin/status'),
  clients: () =>
    request<{ clients: AdminClient[]; asOf: string }>('/api/admin/clients'),
  /** Mint a session for the target user and set the caller's session
   *  cookie to it. After this resolves, navigate to /app — the caller's
   *  browser will be authed as that user. To return to your own account,
   *  sign out and sign back in. */
  impersonate: (userId: string) =>
    request<{ ok: true; asUser: { id: string; email: string } }>(
      '/api/admin/impersonate',
      { method: 'POST', body: JSON.stringify({ userId }) },
    ),
  botResearch: (host: string, path: string, days = 7, limit = 500) =>
    request<BotResearchResponse>(
      `/api/admin/bot-research?host=${encodeURIComponent(host)}&path=${encodeURIComponent(path)}&days=${days}&limit=${limit}`,
    ),
  /**
   * Platform Meta CAPI pixel — fires for every legit click platform-wide.
   * `enabled` is the kill switch. Server NEVER returns the token plaintext;
   * `hasToken` tells the UI whether one is stored so the input can show
   * a "•••• set" placeholder. PUT with `accessToken` omitted preserves the
   * stored token (lets admin toggle `enabled` without re-pasting).
   */
  platformPixel: {
    get: () =>
      request<{
        pixelId: string | null;
        testEventCode: string | null;
        enabled: boolean;
        hasToken: boolean;
        updatedAt: number | null;
      }>('/api/admin/platform-pixel'),
    put: (body: {
      pixelId?: string | null;
      accessToken?: string | null;
      testEventCode?: string | null;
      enabled?: boolean;
    }) =>
      request<{ ok: true }>('/api/admin/platform-pixel', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  },
};

export const integrations = {
  cloudflare: {
    status: () => request<CloudflareIntegration>('/api/integrations/cloudflare'),
    connect: (apiToken: string) =>
      request<CloudflareIntegration>('/api/integrations/cloudflare', {
        method: 'POST',
        body: JSON.stringify({ apiToken }),
      }),
    disconnect: () =>
      request<void>('/api/integrations/cloudflare', { method: 'DELETE' }),
  },
};

// ---------------------------------------------------------------------------
// Org-level Meta Pixel / CAPI (Pro+ feature)
// ---------------------------------------------------------------------------

export interface OrgPixel {
  connected: boolean;
  canEdit: boolean;
  plan: 'free' | 'pro' | 'agency';
  pixelId: string | null;
  testEventCode: string | null;
  lduEu: boolean;
  hasToken: boolean;
  updatedAt: number | null;
}

export const orgPixel = {
  get: () => request<OrgPixel>('/api/org/pixel'),
  /** Upsert. On first save `accessToken` is required; on later saves it's
   *  optional (omitted = preserve stored token, useful for toggling
   *  `lduEu` or editing `testEventCode` without re-pasting). */
  put: (body: {
    pixelId: string;
    accessToken?: string;
    testEventCode?: string | null;
    lduEu?: boolean;
  }) =>
    request<{ ok: true }>('/api/org/pixel', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  disconnect: () =>
    request<{ ok: true }>('/api/org/pixel', { method: 'DELETE' }),
};

// Per-link pixel override. `override: false` means the link inherits the
// org default. Adding an override is identical UX to the org-level form,
// just scoped to one link.
export interface LinkPixel {
  override: boolean;
  canEdit: boolean;
  plan: 'free' | 'pro' | 'agency';
  pixelId: string | null;
  testEventCode: string | null;
  lduEu: boolean;
  hasToken: boolean;
  updatedAt: number | null;
}

export const linkPixel = {
  get: (linkId: string) =>
    request<LinkPixel>(`/api/links/${encodeURIComponent(linkId)}/pixel`),
  put: (
    linkId: string,
    body: {
      pixelId: string;
      accessToken?: string;
      testEventCode?: string | null;
      lduEu?: boolean;
    },
  ) =>
    request<{ ok: true }>(`/api/links/${encodeURIComponent(linkId)}/pixel`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  remove: (linkId: string) =>
    request<{ ok: true }>(`/api/links/${encodeURIComponent(linkId)}/pixel`, {
      method: 'DELETE',
    }),
};
